import type { ActionArgs, LoaderArgs, MetaFunction } from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import { Form, NavLink, useActionData, useLoaderData, useNavigation, useSearchParams } from "@remix-run/react";
import * as React from "react";
import { zfd } from "zod-form-data";
import { AUDIT_EVENT } from "~/modules/audit/audit-events";

import { authenticator } from "~/modules/auth/authenticator";
import { ButtonPrimary } from "~/ui/infra/Button";
import { InputOutlined, InputPasswordOutlined } from "~/ui/infra/Input";
import { useTranslation } from "react-i18next";
import { loginAuthenticator, redirectAfterPasswordLogin } from "~/modules/auth/login-authenticator.server";
import { remixI18next } from "~/config/i18next.server";
import { z } from "zod";
import { FormControl } from "~/ui/infra/FormControl";
import { makeZodI18nMap } from "zod-i18n-map";
import { AuthorizationError } from "remix-auth";
import { getAuditRequestContext } from "~/modules/audit/audit-request.server";
import { recordAuditEventBackground } from "~/models/audit.server";
import {
  authLockoutRetryAfterSeconds,
  authThrottleLockoutFlatten,
  AUTH_THROTTLE_PURPOSE,
  clearAuthThrottle,
  getAuthThrottleStatus,
  registerAuthFailure,
} from "~/models/auth-failure-throttle.server";
import { getUserByEmail } from "~/models/user.server";
import { createCognitoPasswordResetCookie } from "~/sessions/cognito-password-reset-session.server";
import { isCognitoAuthEnabled } from "~/services/cognito.server";
import { PasskeyLoginButton } from "~/ui/auth/PasskeyLoginButton";
import { ClientOnly } from "remix-utils";
import { getShowingAnnouncements } from "~/models/announcement.server";
import { LoginAnnouncements } from "~/ui/infra/dashboard/AnnouncementBanner";
import logger from "~/modules/logger/logger";

export async function loader({ request }: LoaderArgs) {
  await authenticator.isAuthenticated(request, { successRedirect: "/dashboard" });
  // A message must never block sign-in.
  const announcements = await getShowingAnnouncements().catch((error) => {
    logger.warn({ message: "[login] could not load announcements", error });
    return [];
  });
  return json({ cognitoPasskeyEnabled: isCognitoAuthEnabled(), announcements });
}

const schema = zfd.formData({
  email: z.string().email(),
  password: zfd.text(),
  redirectTo: zfd.text().default("/dashboard"),
});

export async function action({ request, context }: ActionArgs) {
  const formData = await request.formData();
  const zodT = await remixI18next.getFixedT(request, "zod_custom");
  const t = await remixI18next.getFixedT(request);

  const formParseResult = await schema.safeParseAsync(formData, {
    errorMap: makeZodI18nMap({ t: zodT }),
  });

  if (!formParseResult.success) {
    recordAuditEventBackground({
      eventName: AUDIT_EVENT.AUTH_LOGIN,
      entity: { type: "session" },
      requestContext: getAuditRequestContext(request, "/login"),
      status: "failure",
      httpStatus: 400,
      errorCode: "VALIDATION_ERROR",
    });
    return json(formParseResult.error.flatten(), { status: 400 });
  }

  const emailNorm = formParseResult.data.email;
  const loginThrottle = await getAuthThrottleStatus(AUTH_THROTTLE_PURPOSE.LOGIN_PASSWORD, emailNorm);
  if (loginThrottle.blocked && loginThrottle.lockedUntil) {
    return json(authThrottleLockoutFlatten(loginThrottle.lockedUntil), {
      status: 429,
      headers: { "Retry-After": String(authLockoutRetryAfterSeconds(loginThrottle.lockedUntil)) },
    });
  }

  try {
    const loginUser = await loginAuthenticator.authenticate("user-password", request, {
      context: { formData, formParseResult: formParseResult.data },
      throwOnError: true,
    });
    const user = await getUserByEmail(formParseResult.data.email);
    await clearAuthThrottle(AUTH_THROTTLE_PURPOSE.LOGIN_PASSWORD, emailNorm);
    recordAuditEventBackground({
      eventName: AUDIT_EVENT.AUTH_LOGIN,
      entity: { type: "user", id: user?.id },
      actor: { userId: user?.id, email: formParseResult.data.email },
      requestContext: getAuditRequestContext(request, "/login"),
      status: "success",
      after: { email: formParseResult.data.email },
    });
    return await redirectAfterPasswordLogin(request, loginUser);
  } catch (error) {
    if (error instanceof Response) {
      return error;
    }
    if (error instanceof AuthorizationError) {
      if (error.message.startsWith("COGNITO_LINK_MISMATCH::")) {
        const mismatchField = error.message.split("::")[1] || "unknown";
        const user = await getUserByEmail(formParseResult.data.email);
        recordAuditEventBackground({
          eventName: AUDIT_EVENT.AUTH_LOGIN,
          entity: { type: "user", id: user?.id },
          actor: { userId: user?.id, email: formParseResult.data.email },
          requestContext: getAuditRequestContext(request, "/login"),
          status: "failure",
          httpStatus: 409,
          errorCode: "COGNITO_LINK_MISMATCH",
          after: { email: formParseResult.data.email, mismatchField },
        });
        return json(
          new z.ZodError([
            {
              code: "custom",
              path: [],
              message: t("app.login_errors.account_identity_mismatch", {
                defaultValue: "Account identity mismatch detected. Please contact support.",
              }),
            },
          ]).flatten(),
          { status: 409 }
        );
      }
      if (error.message === "COGNITO_LINK_BACKFILL_DISABLED") {
        const user = await getUserByEmail(formParseResult.data.email);
        recordAuditEventBackground({
          eventName: AUDIT_EVENT.AUTH_LOGIN,
          entity: { type: "user", id: user?.id },
          actor: { userId: user?.id, email: formParseResult.data.email },
          requestContext: getAuditRequestContext(request, "/login"),
          status: "failure",
          httpStatus: 403,
          errorCode: "COGNITO_LINK_REQUIRED",
          after: { email: formParseResult.data.email },
        });
        return json(
          new z.ZodError([
            {
              code: "custom",
              path: [],
              message: t("app.login_errors.account_not_migrated", {
                defaultValue: "Account is not fully migrated to Cognito. Contact your administrator.",
              }),
            },
          ]).flatten(),
          { status: 403 }
        );
      }
      if (error.message === "ACCOUNT_LOGIN_DISABLED") {
        const user = await getUserByEmail(formParseResult.data.email);
        recordAuditEventBackground({
          eventName: AUDIT_EVENT.AUTH_LOGIN,
          entity: { type: "user", id: user?.id },
          actor: { userId: user?.id, email: formParseResult.data.email },
          requestContext: getAuditRequestContext(request, "/login"),
          status: "failure",
          httpStatus: 403,
          errorCode: "ACCOUNT_LOGIN_DISABLED",
          after: { email: formParseResult.data.email },
        });
        return json(
          new z.ZodError([
            {
              code: "custom",
              path: [],
              message: t("app.login_errors.account_login_disabled", {
                defaultValue: "Unable to sign in. Please contact your administrator.",
              }),
            },
          ]).flatten(),
          { status: 403 }
        );
      }
      if (error.message.startsWith("COGNITO_PASSWORD_RESET_REQUIRED::")) {
        const [, username, challengeSession] = error.message.split("::");
        if (username && challengeSession) {
          await clearAuthThrottle(AUTH_THROTTLE_PURPOSE.LOGIN_PASSWORD, username);
          const setCookieHeader = await createCognitoPasswordResetCookie({
            username,
            challengeSession,
            rememberDevice: false,
          });
          return redirect("/reset-password-first-login", {
            headers: {
              "Set-Cookie": setCookieHeader,
            },
          });
        }
      }
      if (error.message.startsWith("MFA_SETUP_LOCKED::")) {
        const message =
          error.message.split("::")[1] ||
          t("app.login_errors.rate_limited", { defaultValue: "Too many failed attempts. Try again later." });
        const user = await getUserByEmail(formParseResult.data.email);
        recordAuditEventBackground({
          eventName: AUDIT_EVENT.AUTH_LOGIN,
          entity: { type: "user", id: user?.id },
          actor: { userId: user?.id, email: formParseResult.data.email },
          requestContext: getAuditRequestContext(request, "/login"),
          status: "failure",
          httpStatus: 429,
          errorCode: "AUTH_RATE_LIMITED",
          after: { email: formParseResult.data.email, purpose: AUTH_THROTTLE_PURPOSE.MFA_SETUP_TOTP },
        });
        return json(
          new z.ZodError([
            {
              code: "custom",
              path: [],
              message,
            },
          ]).flatten(),
          { status: 429 }
        );
      }
      const errorMessage = error.message || "Incorrect credentials";
      const isProvisioningGapError = errorMessage === "COGNITO_APP_USER_NOT_PROVISIONED";
      const isGenericCredentialError = errorMessage === "Invalid email or password" || isProvisioningGapError;
      const requestContext = getAuditRequestContext(request, "/login");
      const failureActorEmail = formParseResult.data.email;

      if (isGenericCredentialError) {
        const reg = await registerAuthFailure(AUTH_THROTTLE_PURPOSE.LOGIN_PASSWORD, failureActorEmail);
        const userRow = await getUserByEmail(failureActorEmail);
        if (reg.newlyLocked && reg.lockedUntil) {
          recordAuditEventBackground({
            eventName: AUDIT_EVENT.AUTH_THROTTLE_LOCKOUT,
            entity: { type: "user", id: userRow?.id },
            actor: { userId: userRow?.id, email: failureActorEmail },
            requestContext,
            status: "success",
            after: { purpose: AUTH_THROTTLE_PURPOSE.LOGIN_PASSWORD },
          });
        }
        if (reg.blocked && reg.lockedUntil) {
          recordAuditEventBackground({
            eventName: AUDIT_EVENT.AUTH_LOGIN,
            entity: { type: "user", id: userRow?.id },
            actor: { userId: userRow?.id, email: failureActorEmail },
            requestContext,
            status: "failure",
            httpStatus: 429,
            errorCode: "AUTH_RATE_LIMITED",
            after: { email: failureActorEmail },
          });
          return json(authThrottleLockoutFlatten(reg.lockedUntil), {
            status: 429,
            headers: { "Retry-After": String(authLockoutRetryAfterSeconds(reg.lockedUntil)) },
          });
        }
      }

      recordAuditEventBackground({
        eventName: AUDIT_EVENT.AUTH_LOGIN,
        entity: { type: "user" },
        actor: { email: failureActorEmail },
        requestContext,
        status: "failure",
        httpStatus: 400,
        errorCode: isProvisioningGapError
          ? "COGNITO_APP_USER_NOT_PROVISIONED"
          : isGenericCredentialError
            ? "INCORRECT_CREDENTIALS"
            : "AUTH_PROVIDER_ERROR",
        after: { email: failureActorEmail, loginFailureReason: isProvisioningGapError ? "cognito_app_user_missing" : undefined },
      });
      return json(
        new z.ZodError([
          {
            code: "custom",
            path: [],
            message: isGenericCredentialError ? "Incorrect credentials" : errorMessage,
            ...(isGenericCredentialError
              ? {
                  params: {
                    i18n: "errors.incorrect_credentials",
                  },
                }
              : {}),
          },
        ]).flatten(),
        { status: 400 }
      );
    }
    if (error instanceof Error) {
      recordAuditEventBackground({
        eventName: AUDIT_EVENT.AUTH_LOGIN,
        entity: { type: "user" },
        actor: { email: formParseResult.data.email },
        requestContext: getAuditRequestContext(request, "/login"),
        status: "failure",
        httpStatus: 400,
        errorCode: "AUTH_PROVIDER_ERROR",
        after: { email: formParseResult.data.email },
      });
      return json(
        new z.ZodError([
          {
            code: "custom",
            path: [],
            message: error.message,
          },
        ]).flatten(),
        { status: 400 }
      );
    }
  }

  recordAuditEventBackground({
    eventName: AUDIT_EVENT.AUTH_LOGIN,
    entity: { type: "user" },
    actor: { email: formParseResult.data.email },
    requestContext: getAuditRequestContext(request, "/login"),
    status: "failure",
    httpStatus: 400,
    errorCode: "UNKNOWN_LOGIN_ERROR",
    after: { email: formParseResult.data.email },
  });
  return json(
    new z.ZodError([
      {
        code: "custom",
        path: [],
        message: t("app.login_errors.sign_in_failed", { defaultValue: "Sign-in failed. Please try again." }),
      },
    ]).flatten(),
    { status: 400 }
  );
}

export const meta: MetaFunction = () => {
  return {
    title: "Login",
  };
};

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const { cognitoPasskeyEnabled, announcements } = useLoaderData<typeof loader>();
  const redirectTo = searchParams.get("redirectTo") || "/dashboard";
  const authError = searchParams.get("authError");
  const emailRef = React.useRef<HTMLInputElement>(null);
  const passwordRef = React.useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const errors = useActionData<typeof action>();
  const navigation = useNavigation();
  const isSubmittingLogin =
    navigation.state !== "idle" &&
    navigation.formMethod?.toLowerCase() === "post" &&
    (navigation.formAction?.endsWith("/login") ?? false);

  return (
    <>
      <ClientOnly>{() => <LoginAnnouncements announcements={announcements} />}</ClientOnly>
      <p className="mb-2 mt-10 text-center text-xl font-semibold text-light">{t("app.login_header")}</p>
      <Form method="post" className="mx-auto flex max-w-xs flex-col py-6 text-white" noValidate>
        <input type="hidden" name="redirectTo" value={redirectTo} />
        {authError === "missing_login_session" ? (
          <p className="mb-4 text-center text-sm text-red-400">
            {t("app.login_errors.session_expired", { defaultValue: "Session expired. Please log in again." })}
          </p>
        ) : null}
        {authError === "missing_cognito_setup_state" ? (
          <p className="mb-4 text-center text-sm text-red-400">
            {t("app.login_errors.mfa_setup_session_missing", {
              defaultValue: "MFA setup session is missing. Please log in again.",
            })}
          </p>
        ) : null}
        {authError === "mfa_setup_state_expired" ? (
          <p className="mb-4 text-center text-sm text-red-400">
            {t("app.login_errors.mfa_setup_expired", {
              defaultValue: "MFA setup expired. Please log in again to continue.",
            })}
          </p>
        ) : null}
        {authError === "unable_to_select_mfa" ? (
          <p className="mb-4 text-center text-sm text-red-400">
            {t("app.login_errors.unable_to_select_mfa", {
              defaultValue: "Could not continue to MFA. Please try logging in again.",
            })}
          </p>
        ) : null}
        {authError === "unable_to_start_authenticator_setup" ? (
          <p className="mb-4 text-center text-sm text-red-400">
            {t("app.login_errors.unable_to_start_authenticator_setup", {
              defaultValue: "Could not start authenticator setup for this sign-in attempt. Please log in again.",
            })}
          </p>
        ) : null}
        <div className="space-y-4">
          <FormControl>
            <FormControl.Label>{t("common.email")}</FormControl.Label>
            <InputOutlined ref={emailRef} autoFocus name="email" type="email" autoComplete="email" />
            <FormControl.Error>{errors?.fieldErrors.email}</FormControl.Error>
          </FormControl>
          <FormControl>
            <FormControl.Label>{t("common.password")}</FormControl.Label>
            <InputPasswordOutlined ref={passwordRef} name="password" autoComplete="current-password" />
            <FormControl.Error>{errors?.fieldErrors.password}</FormControl.Error>
          </FormControl>
          <FormControl.Error>{errors?.formErrors}</FormControl.Error>
        </div>
        <NavLink to="../forgot-password" className="mb-5 mt-7 block text-center">
          {t("common.forgot_password")}
        </NavLink>
        <ButtonPrimary type="submit" className="my-2 w-full" isLoading={isSubmittingLogin} disabled={isSubmittingLogin}>
          {t("app.login")}
        </ButtonPrimary>
        {cognitoPasskeyEnabled ? (
          <>
            <p className="my-2 text-center text-xs text-secondary-50">
              {t("app.login_errors.or_divider", { defaultValue: "or" })}
            </p>
            <PasskeyLoginButton emailInputRef={emailRef} disabled={isSubmittingLogin} />
          </>
        ) : null}
      </Form>
    </>
  );
}
